import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { CustomersService } from '../customers.service'
import { CustomersRepository } from '../customers.repository'
import { Customer } from '../models/customer.entity'
import { CountriesService, Country } from '../../countries'
import { parseCustomerKeys } from '../libs/customer-search.lib'

interface CustomerDetailState {
  customer?: Customer
  countries: Country[]
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const CUSTOMER_DETAIL_INITIAL_STATE: CustomerDetailState = {
  countries: [],
  isLoading: false,
  isModified: false,
}

@Injectable()
export class CustomerDetailStore {
  constructor(
    private customersService: CustomersService,
    private customersRepository: CustomersRepository,
    private countriesService: CountriesService,
  ) {}

  private state = signal<CustomerDetailState>(CUSTOMER_DETAIL_INITIAL_STATE)

  // Selectors
  readonly customer = computed(() => this.state().customer)
  readonly shippingAddress = computed(() => this.customer()?.shippingAddress)
  readonly countries = computed(() => this.state().countries)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(customerId?: string): Promise<void> {
    if (!customerId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.customersService.read$(customerId).subscribe({
        next: async (data) => {
          const extData = await this.getCountries(
            parseCustomerKeys(data).countryCodes,
          )
          this.setData(
            data,
            this.state().customer?._id
              ? this.state().customer?._id !== data._id
              : false,
            extData,
          )
          resolve()
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(customer: Customer, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.customersService.upsert$(customer).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, false)
          if (notify === true) {
            this.customersRepository.notify({ code: 'CUSTOMER_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async getCountries(countryCodes?: string[]): Promise<Country[] | undefined> {
    if (!countryCodes?.length) {
      return undefined
    }
    return new Promise((resolve) => {
      this.countriesService.list$({ alpha2Code: countryCodes }).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(
    customer: Customer,
    isModified?: boolean,
    extData?: Country[] | undefined,
  ) {
    this.state.set({
      ...this.state(),
      customer,
      isModified: isModified ?? this.state().isModified,
      countries: extData ?? [],
      isLoading: false,
      error: undefined,
    })
  }

  setCustomer(customer: Customer, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      customer,
      isModified: isModified ?? true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
